// The put-forward's rules, outside the dialog — T42 (5 Sep 2026).
//
// The pure half: how long a note may be (pinned to the service's cap by `submission.test.ts`),
// how a mandate is named in the dialog, and the one sentence the consent line says on the
// candidate page. Here because it is what a test can hold.
import type { CandidatePipelineRow, ConsentRow } from '@vera/bff';

/** Restated from `@vera/bff`'s `MAX_SUBMISSION_NOTE`, pinned by the test. */
export const SUBMISSION_NOTE_MAX = 2000;

/** "<title> at <client>" — the mandate as the dialog names it; without a client, the title alone. */
export function mandateLabel(
  row: Pick<CandidatePipelineRow, 'searchTitle' | 'clientName'>,
): string {
  return row.clientName ? `${row.searchTitle} at ${row.clientName}` : row.searchTitle;
}

/**
 * The consent line under "On these mandates". The latest capture is what counts — a withdrawal
 * after a grant is a later row with `granted: false`, and it reads as no consent, not as a
 * recorded one — and a candidate with no capture at all says so rather than showing a blank.
 */
export function consentLine(
  consent: ConsentRow | null,
  formatDate: (iso: string) => string,
  consentSearchIds: readonly string[] = [],
): string {
  if (!consent || !consent.granted) {
    // T167i (2 Oct 2026, "That role only"): a consent for one Search alone, said as that.
    return consentSearchIds.length > 0
      ? 'Consent for one Search alone, from their yes to its role'
      : 'No consent recorded';
  }
  return `Consent to be put forward · recorded by ${consent.capturedBy} on ${formatDate(consent.capturedAt)}`;
}

/**
 * T554 (30 Oct 2026): whether they said no to being put forward, the one thing that stops the
 * dialog: the latest consent is a no, and since T167i (2 Oct 2026, "That role only") no consent
 * for this Search alone covers it (`consentSearchIds`, from a yes to the role the Search was
 * opened from). No consent recorded is not a no. A no for this Search alone
 * (`consentRefusedSearchIds`, the newest capture covering it a no) is a no too (T554c, 31 Oct
 * 2026): the dialog greys Confirm out, as the service would refuse it (`consent_refused`).
 */
export const saidNo = (
  consent: ConsentRow | null,
  consentSearchIds: readonly string[] = [],
  searchId: string | null = null,
  consentRefusedSearchIds: readonly string[] = [],
): boolean =>
  (searchId !== null && consentRefusedSearchIds.includes(searchId)) ||
  (consent?.granted === false && !(searchId !== null && consentSearchIds.includes(searchId)));

/** Restated from `@vera/bff`'s `FIT_SCORE_MIN`/`FIT_SCORE_MAX`: kadmon's fit, out of 10, whole (T125). */
export const FIT_SCORE_MIN = 1;
export const FIT_SCORE_MAX = 10;

/**
 * What the score box sends with a put-forward — T125 (6 Oct 2026, "Dialog and Approve"): the
 * number typed, when it is a whole number from 1 to 10, even kadmon's own, since the number the
 * dialog showed can be older than the confirm and what the team typed is what they confirmed (the
 * service then records whoever typed it as its setter). Nothing when the box is
 * empty, so the service fixes the recipe's number as it stands. `invalid` when the box holds
 * anything else, which keeps Confirm from sending it.
 */
export function typedFitScore(typed: string): { send: number | undefined; invalid: boolean } {
  const text = typed.trim();
  if (text === '') return { send: undefined, invalid: false };
  const value = Number(text);
  if (!/^\d+$/.test(text) || value < FIT_SCORE_MIN || value > FIT_SCORE_MAX) {
    return { send: undefined, invalid: true };
  }
  return { send: value, invalid: false };
}

/**
 * Whether Approve's "Ask them on WhatsApp" may go with this box (T125, the review of 6 Oct 2026):
 * only empty. An ask records no put-forward; the one made when they say yes fixes the recipe's
 * number, so a typed one would be dropped without a word. The team clears the box to ask, or
 * records the consent and confirms with it.
 */
export function askKeepsTheBox(typed: string): boolean {
  return typed.trim() === '';
}
